import axios from "axios";
import buildUrl from "build-url-ts";
import isEqual from "lodash/isEqual";
import {
  DomainsListResponse,
  SeekIndexRequest,
  SeekIndexResponse,
} from "src/types/experiences/storyboardSettings";
import { StoryboardResponse, StoryboardUpdateRequest } from "src/types/experiences/storyboardSettings/storyboard";

const BASE_URL = process.env.REACT_APP_CLEVERSTORY_API_URL;
const apiV2 = `${BASE_URL}/api/2.0`;

const normalizeStoryboardResponse = (data: any): StoryboardResponse => {
  if (data && typeof data === "object" && "passwordProtected" in data) {
    const { passwordProtected, ...rest } = data;
    return { ...rest, passwordEnabled: Boolean(passwordProtected) };
  }
  return data;
};

export const computePatch = (
  original: StoryboardResponse,
  draft: StoryboardResponse,
): Partial<StoryboardResponse> => {
  const patch: Partial<StoryboardResponse> = {};
  (Object.keys(draft) as (keyof StoryboardResponse)[]).forEach((key) => {
    if (!isEqual(original[key], draft[key])) {
      (patch as any)[key] = draft[key];
    }
  });

  if ("darkViewer" in patch && draft.viewer !== undefined) {
    patch.viewer = draft.viewer;
  }

  return patch;
};

export const buildUpdatePayload = (
  draft: StoryboardUpdateRequest,
): StoryboardUpdateRequest => {
  const payload: StoryboardUpdateRequest = { ...draft };

  delete (payload as any).modifiedDate;

  if (draft.gatingConfigurations) {
    payload.gatingConfigurations = draft.gatingConfigurations.map((g) => {
      const { pageId, ...rest } = g;

      const normalized: any = { ...rest };

      if (normalized.page === undefined) {
        normalized.page = null;
      }

      const gatingId = normalized.gating?.id;
      if (gatingId) {
        normalized.gating = gatingId;
      } else {
        normalized.gating = null;
      }

      if (pageId && !normalized.target) {
        normalized.target = { id: pageId, type: "page" };
      }
      if (normalized.target && typeof normalized.target === "object") {
        const { id, name, type, entityType } = normalized.target;
        normalized.target = { id, name, type: type ?? entityType ?? "page" };
      }

      return normalized;
    });
  }

  if (draft.appearance) {
    const normalizeColor = (c: any) => {
      if (!c || typeof c !== "object") return c;
      return { type: "solid", ...c };
    };

    const mediaToId = (m: any) => {
      if (!m) return null;
      const id = typeof m === "string" ? m : m.id;
      return id ?? null;
    };

    const normalizeBackground = (bg: any) => {
      if (!bg) return bg;
      const isImage = bg.type === "image" || bg.type === "video";
      const next: any = { ...bg };
      if (bg.color && typeof bg.color === "object") {
        const rgba = { ...bg.color };
        delete rgba.type;
        next.color = rgba;
      }
      if (isImage) {
        next.type = "image";
        next.media = mediaToId(bg.media);
        next.type = "image";
      } else {
        delete next.media;
      }
      return next;
    };

    const background = normalizeBackground(draft.appearance.background);
    const rawSlideBg = draft.appearance.slideBackground;
    const slideBackground = rawSlideBg
      ? { ...rawSlideBg, media: mediaToId(rawSlideBg.media) }
      : rawSlideBg;
    const iconColor = normalizeColor(draft.appearance.iconColor);

    payload.appearance = {
      ...draft.appearance,
      background,
      slideBackground,
      iconColor,
    };
  }

  if (draft.domain && typeof draft.domain === "object") {
    payload.domain = draft.domain.id as any;
  }

  // The server expects the vanity URL as a flat `alias` field, not the whole
  // page object. computePatch surfaces the changed `page` object, so flatten it.
  if (draft.page && typeof draft.page === "object") {
    delete payload.page;
    if (draft.page.canonical !== undefined) {
      payload.alias = draft.page.canonical;
    }
  }

  if (draft.social && typeof draft.social === "object") {
    payload.social = draft.social.id as any;
  }

  return payload;
};

export const websiteSettingsApi = {
  fetchStoryboard: async (id: string): Promise<StoryboardResponse> => {
    const url = buildUrl(`${apiV2}/storyboards/${id}`);
    const response = await axios.get(url);
    return normalizeStoryboardResponse(response.data);
  },

  updateStoryboard: async (
    payload: { id: string; data: StoryboardUpdateRequest },
  ): Promise<StoryboardResponse> => {
    const url = buildUrl(`${apiV2}/storyboards/${payload.id}`);
    const body = buildUpdatePayload(payload.data);
    const response = await axios.put(url, body);
    return normalizeStoryboardResponse(response.data);
  },

  fetchDomains: async (): Promise<DomainsListResponse> => {
    const url = buildUrl(`${apiV2}/workspaces/domains`);
    const response = await axios.get(url);
    return response.data;
  },

  unpublishPage: async (pageId: string): Promise<unknown> => {
    const url = buildUrl(`${apiV2}/pages/${pageId}/unpublish`);
    const response = await axios.post(url);
    return response.data;
  },

  deleteStoryboard: async (id: string): Promise<unknown> => {
    const url = buildUrl(`${apiV2}/storyboards/${id}`);
    const response = await axios.delete(url);
    return response.data;
  },

  indexPageForSeek: async (
    payload: { pageId: string; data: SeekIndexRequest },
  ): Promise<SeekIndexResponse> => {
    const url = buildUrl(`${apiV2}/cleverstory/seek/index/page`);
    const response = await axios.post(url, payload.data);
    return response.data;
  },

  indexStoryboardForSeek: async (
    payload: { storyboardId: string; data: SeekIndexRequest },
  ): Promise<SeekIndexResponse> => {
    const url = buildUrl(`${apiV2}/cleverstory/seek/index/storyboard`);
    const response = await axios.post(url, payload.data);
    return response.data;
  },
};
